/**
 * useHospitalForm - Hook for hospital form editing and validation
 *
 * Manages:
 * - Edit mode state
 * - Form data and field changes
 * - Validation (email, CPF, CNPJ)
 * - Save and cancel operations
 */
import { useState } from 'react';
import { msg, MESSAGES } from '../utils/messages';
import estabelecimentoService from '../services/estabelecimentoService';
import type { Hospital } from './useHospitalData';
import useAuthStore from '../stores/authStore';

// Validation functions
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateCNPJ = (cnpj: string): boolean => {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  let sum = 0;
  let weight = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weight[i];
  }
  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(cleaned[12]) !== digit) return false;

  sum = 0;
  weight = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weight[i];
  }
  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return parseInt(cleaned[13]) === digit;
};

export const validateCPF = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(cleaned[9]) !== digit) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return parseInt(cleaned[10]) === digit;
};

// Format functions
export const formatTelefone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 10) {
    return cleaned
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    return cleaned
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  }
};

export const formatCEP = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9);
};

export const formatCNPJ = (value: string): string => {
  // Extract digits for the base CNPJ (first 14 digits)
  const allDigits = value.replace(/\D/g, '');
  const baseDigits = allDigits.slice(0, 14);

  // Format base CNPJ
  let formatted = baseDigits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');

  // If we have a fully formatted CNPJ (18 chars = XX.XXX.XXX/XXXX-XX),
  // allow suffix (free text after the mask)
  if (baseDigits.length === 14) {
    // Get any characters typed after the 14th digit position in the original value
    // Find where the base mask ends and append the rest
    const fullMask = formatted; // 18 chars
    const originalAfterMask = value.slice(fullMask.length);
    if (originalAfterMask) {
      formatted += originalAfterMask;
    }
  }

  return formatted;
};

export const formatCPF = (value: string): string => {
  const allDigits = value.replace(/\D/g, '');
  const baseDigits = allDigits.slice(0, 11);

  let formatted = baseDigits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');

  // If fully formatted CPF (14 chars = XXX.XXX.XXX-XX), allow suffix
  if (baseDigits.length === 11) {
    const originalAfterMask = value.slice(formatted.length);
    if (originalAfterMask) {
      formatted += originalAfterMask;
    }
  }

  return formatted;
};

interface UseHospitalFormProps {
  selectedHospital: Hospital | null;
  setSelectedHospital: (hospital: Hospital | null) => void;
  updateHospitalInList: (hospital: Hospital) => void;
  addHospitalToList: (hospital: Hospital) => void;
}

export function useHospitalForm({
  selectedHospital,
  setSelectedHospital,
  updateHospitalInList,
  addHospitalToList,
}: UseHospitalFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedHospital, setEditedHospital] = useState<Hospital | null>(null);
  const [saving, setSaving] = useState(false);

  // Current hospital (edited or selected)
  const currentHospital = isEditing ? editedHospital : selectedHospital;

  // Start editing existing hospital
  const handleEdit = () => {
    if (selectedHospital) {
      setEditedHospital({ ...selectedHospital });
      setIsEditing(true);
    }
  };

  // Start creating new hospital
  const handleCreateNew = () => {
    const newHospital: Hospital = {
      id: 0,
      codigo: '',
      pessoa: 'JURIDICA',
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      endereco: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
      telefone: '',
      ramal: '',
      email: '',
      periodicidade: '',
      observacoes: '',
      ativo: true,
    };
    // Limpar seleção do combobox ao criar novo
    setSelectedHospital(null);
    setEditedHospital(newHospital);
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedHospital(null);
    setIsEditing(false);
  };

  // Update field
  const handleFieldChange = (field: keyof Hospital, value: any) => {
    if (editedHospital) {
      setEditedHospital({ ...editedHospital, [field]: value });
    }
  };

  // Save hospital
  const handleSave = async () => {
    if (!editedHospital) return;

    // Validate email
    if (editedHospital.email && !validateEmail(editedHospital.email)) {
      msg.warning(MESSAGES.VALIDATION.INVALID_EMAIL);
      return;
    }

    // Validate CPF (only for pessoa FISICA)
    if (editedHospital.cnpj && editedHospital.pessoa === 'FISICA') {
      const cnpjLimpo = editedHospital.cnpj.replace(/\D/g, '');
      if (cnpjLimpo.length >= 11) {
        // Extract first 11 digits for CPF validation
        const cpfDigits = cnpjLimpo.slice(0, 11);
        if (!validateCPF(cpfDigits)) {
          msg.warning(MESSAGES.VALIDATION.INVALID_CPF);
          return;
        }
      }
    }

    const isCreating = editedHospital.id === 0;

    // Story 2.26: guard defensivo — backend já valida (403), mas evita round-trip
    const { podeEditar, podeCriar } = useAuthStore.getState();
    const podeOperar = isCreating ? podeCriar('HOSPITAIS_LIST') : podeEditar('HOSPITAIS_LIST');
    if (!podeOperar) {
      const acao = isCreating ? 'criar' : 'editar';
      msg.error(`Você não tem permissão para ${acao} hospitais. Solicite acesso ao administrador (Master).`);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        codigo: editedHospital.codigo,
        razao_social: editedHospital.razao_social,
        nome_fantasia: editedHospital.nome_fantasia,
        pessoa: editedHospital.pessoa,
        cnpj: editedHospital.cnpj || '',
        endereco: editedHospital.endereco,
        bairro: editedHospital.bairro,
        cidade: editedHospital.cidade,
        estado: editedHospital.estado,
        cep: editedHospital.cep,
        telefone: editedHospital.telefone,
        ramal: editedHospital.ramal,
        email: editedHospital.email,
        periodicidade: editedHospital.periodicidade,
        observacoes: editedHospital.observacoes,
        ativo: editedHospital.ativo,
      };

      if (isCreating) {
        const newHospital = await estabelecimentoService.createEstabelecimento(payload);
        addHospitalToList(newHospital);
        setIsEditing(false);
        setEditedHospital(null);
        msg.success(MESSAGES.HOSPITAL.CREATED);
      } else {
        const updatedHospital = await estabelecimentoService.updateEstabelecimento(editedHospital.id, payload);
        updateHospitalInList(updatedHospital);
        setIsEditing(false);
        setEditedHospital(null);
        msg.success(MESSAGES.HOSPITAL.UPDATED);
      }
    } catch (err: any) {
      console.error('Erro ao salvar hospital:', err);

      // Extrair mensagem de erro do formato Pydantic/Django Ninja
      let errorMessage: string;
      const detail = err.response?.data?.detail;

      if (Array.isArray(detail)) {
        // Formato Pydantic: [{type, loc, msg}, ...]
        errorMessage = detail.map((e: any) => {
          const field = e.loc?.slice(-1)?.[0] || 'Campo';
          return `${field}: ${e.msg}`;
        }).join(', ');
      } else if (typeof detail === 'string') {
        // Formato simples: string
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail !== null) {
        // Formato objeto: {type, loc, msg}
        errorMessage = detail.msg || JSON.stringify(detail);
      } else {
        errorMessage = isCreating ? MESSAGES.HOSPITAL.CREATE_ERROR : MESSAGES.HOSPITAL.SAVE_ERROR;
      }

      msg.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return {
    // State
    isEditing,
    editedHospital,
    setEditedHospital,
    saving,
    currentHospital,

    // Actions
    handleEdit,
    handleCreateNew,
    handleCancel,
    handleFieldChange,
    handleSave,
  };
}
